/**
 * useVechainWallet Hook
 *
 * Custom React hook for managing VeChain wallet data and state
 */

import { useQuery } from '@tanstack/react-query';
import { vechainAPI, hexWeiToVet } from '../services/vechainApi';
import type { VeChainWalletData } from '../types/vechain.types';

/**
 * Fetch VET and VTHO prices
 */
export const useVechainPrices = () => {
  return useQuery({
    queryKey: ['vechain', 'prices'],
    queryFn: () => vechainAPI.getPrices(),
    staleTime: 60_000,
    refetchInterval: 2 * 60_000,
  });
};

/**
 * Fetch wallet data for multiple addresses
 */
export const useVechainWallets = (addresses: string[]) => {
  const { data: prices = { vetPrice: 0, vthoPrice: 0 } } = useVechainPrices();

  const {
    data: wallets,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['vechain', 'wallets', addresses, prices],
    queryFn: () => vechainAPI.getMultipleWalletsData(addresses, prices.vetPrice, prices.vthoPrice),
    enabled: addresses.length > 0 && prices.vetPrice > 0,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

  // Calculate aggregated metrics
  const totalVetBalance = wallets?.reduce((sum, w) => sum + w.vetBalance, 0) || 0;
  const totalVetBalanceUsd = wallets?.reduce((sum, w) => sum + w.vetBalanceUsd, 0) || 0;
  const totalVthoBalance = wallets?.reduce((sum, w) => sum + w.vthoBalance, 0) || 0;
  const totalVthoBalanceUsd = wallets?.reduce((sum, w) => sum + w.vthoBalanceUsd, 0) || 0;
  const totalBalanceUsd = wallets?.reduce((sum, w) => sum + w.totalBalanceUsd, 0) || 0;
  const totalDailyVTHO = wallets?.reduce((sum, w) => sum + w.dailyVTHOGeneration, 0) || 0;
  const totalMonthlyVTHO = wallets?.reduce((sum, w) => sum + w.monthlyVTHOGeneration, 0) || 0;

  // Count unique tokens
  const uniqueTokens = new Set<string>();
  wallets?.forEach(w => w.tokens.forEach(t => uniqueTokens.add(t.address)));

  // Count nodes
  const nodeCount = wallets?.filter(w => w.nodeType !== null).length || 0;

  return {
    wallets: wallets || [],
    isLoading,
    error,
    refetch,
    vetPrice: prices.vetPrice,
    vthoPrice: prices.vthoPrice,
    // Aggregated metrics
    totalVetBalance,
    totalVetBalanceUsd,
    totalVthoBalance,
    totalVthoBalanceUsd,
    totalBalanceUsd,
    totalDailyVTHO,
    totalMonthlyVTHO,
    totalTokens: uniqueTokens.size,
    nodeCount,
  };
};
